import { z } from 'zod/v4';

import { normalizeFormWidgetArgs } from './form-widget-args.normalization';
import { partialFormWidgetDataSchema } from './partial-form-widget-data.schema';

/**
 * Two tightenings over the shared partial, both because `apps/mcp-backend` has **no
 * TranslateService** and the entity factory takes its human-readable strings from the host's i18n
 * (`CreateLegacyFormWidgetOptions.texts`). Called without them — which is the only way this service
 * can call it — the factory produces `label: ''` for the submit button and `subscriptionMessage: ''`
 * for the post-submit message. Both render: an unlabelled button, and a visitor who submits the form
 * and is shown an empty box. Neither fails validation anywhere, which is what makes them worth
 * gating here.
 *
 * `fields` is deliberately NOT required, unlike social's `socials`: the factory's default single
 * required email field is a complete, working subscription form, not a placeholder.
 */
const createFormWidgetDataSchema = partialFormWidgetDataSchema
    .extend({
        buttonLabel: z
            .string()
            .min(1)
            .optional()
            .describe('REQUIRED. Text on the submit button, in the language of the page — e.g. "Подписаться".'),
    })
    .superRefine((data, ctx) => {
        // Both requirements are checked HERE rather than by making `buttonLabel` a required field,
        // so that a call missing both is told about both at once.
        //
        // Measured 2026-08-28 on a live run: zod skips `superRefine` entirely when the base object
        // fails, so a required `buttonLabel` produced a staircase — the model sent no `data`, was told
        // only about `buttonLabel`, sent it, was then told about the outcome, and spent three
        // apply_operations calls on one form. The `required` array in the published JSON Schema did
        // not prevent that first call either, which is what settles the trade: the advertised
        // requiredness bought nothing here, the combined error saves a round trip.
        if (!data.buttonLabel?.trim()) {
            ctx.addIssue({
                code: 'custom',
                message:
                    'a new form needs buttonLabel: the text on its submit button, in the language of ' +
                    'the page. Without it the button renders empty.',
                path: ['buttonLabel'],
            });
        }

        if (data.successMessage === undefined && data.successLink === undefined) {
            ctx.addIssue({
                code: 'custom',
                // Names both fields: a message that said only "required" would leave the model
                // guessing which of the two this widget wanted.
                message:
                    'a new form needs a post-submit outcome: send successMessage (what the visitor is ' +
                    'shown) or successLink (where they are sent). Without either, submitting the form ' +
                    'shows an empty message.',
                path: ['successMessage'],
            });
        }
    });

const createFormWidgetObjectSchema = z.object({
    columnId: z.string().describe('Id of the column to create the subscription form in.'),
    index: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("Position within the column's widget list (0-based). Omit to append at the end."),
    data: createFormWidgetDataSchema.describe(
        'Form data. buttonLabel and a post-submit outcome are required; the fields default to a single ' +
            'required email input, and all styling falls back to the site defaults.'
    ),
});

export const createFormWidgetArgsSchema = z.preprocess(normalizeFormWidgetArgs, createFormWidgetObjectSchema);
